import { FC, Fragment, VFC } from "react";

import { AddBoxOutlined } from "@material-ui/icons";
import {
  CommoditiesView,
  DimensionsView,
  FreightClassView,
  NmfcCodeView,
  PackingCountView,
  PackingTypeView,
  WeightView,
} from "app/pages/shipments/components/EditableShipmentPackageGroupElements";
import { ShipmentDimUnit, ShipmentPackageGroup, ShipmentPackagingType } from "app/pages/shipments/types/domain";
import { useTranslation } from "react-i18next";

import { useShipmentDetails } from "../provider/ShipmentDetailsProvider";
import { ShipmentCargoDetailsView } from "./ShipmentCargoDetailsView";

const CargoDetailsHeader: VFC<{ index: number; onRemoveClick(index: number): void }> = ({ index, onRemoveClick }) => {
  const { t } = useTranslation("shipments");

  return (
    <div className="flex gap-2 justify-between p-[16px] bg-white text-[14px] items-center border-b border-border">
      <div className="font-bold text-[16px]">{t("shipmentDetails_packageGroup_itemNo", { number: index + 1 })}</div>
      <button type="button" className="text-gray-500 hover:text-brandBlue" onClick={() => onRemoveClick(index)}>
        {t("shipmentDetails_removePackageGroupButton_button")}
      </button>
    </div>
  );
};

const CargoDetailsRow: FC<{ alignItems?: "start" | "end" }> = ({ children, alignItems = "end" }) => (
  <div className={`flex flex-wrap gap-2 p-[16px] odd:bg-zebra text-[14px] items-${alignItems}`}>{children}</div>
);

const createPackageGroup = (): ShipmentPackageGroup => ({
  id: window.crypto.randomUUID(),
  dimUnit: ShipmentDimUnit.IN,
  heightPerPackage: 1,
  itemQuantity: 1,
  lengthPerPackage: 1,
  packagingType: ShipmentPackagingType.Other,
  weightPerPackage: 1,
  widthPerPackage: 1,
});

const ShipmentDetailsCargoDetailsView: VFC = () => {
  const { isEditing, patch, patchedShipment, errors } = useShipmentDetails();
  const { t } = useTranslation(["common", "shipper", "shipments"]);

  const handlePackageGroupChange =
    <K extends keyof ShipmentPackageGroup>(index: number, key: K) =>
    (value: ShipmentPackageGroup[K]) => {
      const packageGroups = structuredClone(patchedShipment?.loadSpec?.packageGroups) || [];
      packageGroups[index] = { ...(packageGroups[index] || {}), [key]: value };
      patch({ loadSpec: { packageGroups } });
    };

  const handlePackageGroupRemove = (index: number) => {
    patch({ loadSpec: { packageGroups: patchedShipment.loadSpec.packageGroups?.filter((_, i) => i !== index) } });
  };

  const handleAddPackageGroup = () => {
    patch({ loadSpec: { packageGroups: [...(patchedShipment.loadSpec.packageGroups || []), createPackageGroup()] } });
  };

  return (
    <>
      {isEditing ? (
        <>
          {patchedShipment.loadSpec.packageGroups?.map((packageGroup, index) => (
            <Fragment key={packageGroup.id}>
              <CargoDetailsHeader index={index} onRemoveClick={handlePackageGroupRemove} />
              <div className="border-b border-border">
                <CargoDetailsRow alignItems="start">
                  <PackingTypeView
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                  <PackingCountView
                    errors={errors}
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                </CargoDetailsRow>
                <CargoDetailsRow>
                  <DimensionsView
                    errors={errors}
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                </CargoDetailsRow>
                <CargoDetailsRow>
                  <WeightView
                    errors={errors}
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                </CargoDetailsRow>
                <CargoDetailsRow>
                  <FreightClassView
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                  <NmfcCodeView
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                </CargoDetailsRow>
                <CargoDetailsRow>
                  <CommoditiesView
                    onChange={handlePackageGroupChange}
                    packageGroup={packageGroup}
                    packageGroupIndex={index}
                  />
                </CargoDetailsRow>
              </div>
            </Fragment>
          ))}
          <div className="flex justify-end p-[16px]">
            <button
              type="button"
              onClick={handleAddPackageGroup}
              className="flex gap-1 items-center font-bold text-brandBlue"
            >
              <AddBoxOutlined />
              <span>{t("shipper:addAnotherItem")}</span>
            </button>
          </div>
        </>
      ) : (
        <ShipmentCargoDetailsView package_groups={patchedShipment.loadSpec.packageGroups ?? []} />
      )}
    </>
  );
};

export default ShipmentDetailsCargoDetailsView;
